import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Upload, X, Loader2, CheckCircle, AlertCircle, ImagePlus } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
const MAX = 10 * 1024 * 1024;
const okType = (f) => TYPES.includes(f.type) || /\.(heic|heif)$/i.test(f.name);


export const BulkDeliveryUpload = ({ onDone }) => {
  const [items, setItems] = useState([]);
  const [running, setRunning] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => () => items.forEach((it) => URL.revokeObjectURL(it.preview)), [items]);

  const addFiles = (files) => {
    const next = Array.from(files).map((file) => ({
      key: `${file.name}-${file.size}-${Math.random()}`,
      file,
      preview: URL.createObjectURL(file),
      status: !okType(file) ? 'error' : file.size > MAX ? 'error' : 'ready',
      error: !okType(file) ? 'JPG, PNG, WebP ou HEIC seulement' : file.size > MAX ? 'Max 10 Mo' : '',
    }));
    setItems((prev) => [...prev, ...next]);
    if (inputRef.current) inputRef.current.value = '';
  };

  const update = (key, patch) => setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  const remove = (key) => setItems((prev) => prev.filter((it) => it.key !== key));

  const pending = items.filter((it) => it.status === 'ready' || it.status === 'failed');

  const uploadAll = async () => {
    setRunning(true);
    for (const it of [...pending].reverse()) {
      update(it.key, { status: 'uploading', error: '' });
      const fd = new FormData();
      fd.append('file', it.file);
      try {
        await axios.post(`${API}/admin/deliveries`, fd, { withCredentials: true });
        update(it.key, { status: 'done' });
      } catch (ex) {
        update(it.key, { status: 'failed', error: ex.response?.data?.detail || 'Échec' });
      }
    }
    setRunning(false);
    onDone();
    setTimeout(() => setItems((prev) => prev.filter((it) => it.status !== 'done')), 1500);
  };

  return (
    <div className="bg-gray-50 border rounded-xl p-4 space-y-4" data-testid="bulk-upload">
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className="border-2 border-dashed border-gray-300 hover:border-[#1a5c3b] rounded-xl p-6 text-center cursor-pointer transition-colors bg-white"
        data-testid="bulk-dropzone"
      >
        <ImagePlus className="w-8 h-8 mx-auto text-[#1a5c3b] mb-2" />
        <p className="font-semibold text-gray-800">Glissez vos photos ici ou cliquez pour en choisir plusieurs</p>
        <p className="text-xs text-gray-500">JPG, PNG, WebP ou HEIC (iPhone), max 10 Mo chacune</p>
        <input ref={inputRef} type="file" multiple accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" className="hidden" onChange={(e) => addFiles(e.target.files)} data-testid="bulk-file-input" />
      </div>

      {items.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {items.map((it) => (
              <div key={it.key} className="flex items-center gap-3 bg-white border rounded-lg p-2" data-testid="bulk-item">
                <img src={it.preview} alt="" className="w-20 h-14 object-cover rounded shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-700 truncate">{it.file.name}</p>
                  {it.error && <p className="text-xs text-red-600 mt-1">{it.error}</p>}
                </div>
                {it.status === 'uploading' && <Loader2 className="w-5 h-5 animate-spin text-[#1a5c3b]" />}
                {it.status === 'done' && <CheckCircle className="w-5 h-5 text-green-600" />}
                {(it.status === 'failed' || it.status === 'error') && <AlertCircle className="w-5 h-5 text-red-500" />}
                {it.status !== 'uploading' && it.status !== 'done' && (
                  <button type="button" onClick={() => remove(it.key)} className="p-1 text-gray-400 hover:text-gray-700" data-testid="bulk-item-remove"><X className="w-4 h-4" /></button>
                )}
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-gray-500">{`${pending.length} photo(s) prête(s)`}</p>
            <Button onClick={uploadAll} disabled={running || pending.length === 0} className="bg-[#1a5c3b]" data-testid="bulk-upload-btn">
              {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Ajouter {pending.length > 0 ? `${pending.length} ` : ''}livraison(s)
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
